import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { googleAnalyticsDimension, googleAnalyticsEvent } from "../../../gtag";
import {
  Button,
  BUTTON_TYPES,
  Modal,
  PreLoading,
  RatingStars,
  SvgIcons,
} from "@tripian/react";
import Model, { helper, Providers } from "@tripian/model";
import useUser from "../../../hooks/useUser";
import { useGygApi } from "../../../hooks/useGygApi";
import { TRIPS } from "../../../constants/ROUTER_PATH_TITLE";
import { useNavigate } from "react-router-dom";
import GygTourInfoForm from "./GygTourInfoForm/GygTourInfoForm";
import GygTourOption from "./GygTourInfoForm/GygTourOption/GygTourOption";
import moment from "moment";
import ImageGallery from "../../../components/ImageGallery/ImageGallery";
import GygTourCheckout from "./GygTourInfoForm/GygTourCheckout/GygTourCheckout";

interface IGygTourInfo {
  cityId: number;
  cityName?: string;
  tourId: number;
  startDate?: string;
  endDate?: string;
  adults?: number;
  children?: number;
  isOpen: boolean;
  onClose: () => void;
  t: (value: Model.TranslationKey) => string;
}

const GygTourInfo: React.FC<IGygTourInfo> = ({
  cityId,
  cityName,
  tourId,
  startDate,
  endDate,
  adults,
  children,
  isOpen,
  onClose,
  t,
}) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [selectedTourOptionId, setSelectedTourOptionId] = useState<
    number | null
  >(null);
  const [selectedDateTime, setSelectedDateTime] = useState<string>("");

  const leftRef = useRef<HTMLDivElement>(null);
  const [leftHeight, setLeftHeight] = useState<number | undefined>(undefined);
  const formRef = useRef<HTMLDivElement>(null);
  const [formsHeight, setFormsHeight] = useState<number | undefined>(undefined);
  const availabilityRef = useRef<HTMLDivElement>(null);

  const [availableDate, setAvailableDate] = useState<string | undefined>(
    undefined
  );
  const [personsCategories, setPersonsCategories] = useState<
    (Providers.Gyg.PersonCategory & { count: number })[]
  >([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  const { user } = useUser();

  const navigate = useNavigate();

  const {
    fetchGygTourInfo,
    gygTourData,
    gygTourBooking,
    // gygBookingInfo,
    gygLoaders,
    confirmPayment,
    successfullBooking,
    fetchGygTourOptions,
    fetchGygTourPriceBreakdown,
    gygTourPriceBreakdown,
    gygTourOptions,
    errorMessage,
  } = useGygApi(cityId, cityName);

  useEffect(() => {
    if (isOpen) {
      fetchGygTourInfo(tourId.toString());
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, startDate, endDate, tourId, fetchGygTourInfo]);

  useEffect(() => {
    if (leftRef.current) {
      setLeftHeight(leftRef.current.offsetHeight);
    }
    if (formRef.current) {
      setFormsHeight(formRef.current.offsetHeight);
    }
  }, [gygTourData, gygLoaders.tourDataLoader, isGalleryOpen]);

  useEffect(() => {
    // Only redirect if there's an error and tour data failed to load
    if (errorMessage && !gygLoaders.tourDataLoader && !gygTourData) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(TRIPS.PATH, { replace: true });
      }
    }
  }, [errorMessage, navigate, gygLoaders.tourDataLoader, gygTourData]);

  useEffect(() => {
    if (gygTourData) {
      googleAnalyticsDimension("viewedProductUrl", gygTourData?.tour.url);
    }
  }, [gygTourData]);

  useEffect(() => {
    if (successfullBooking) {
      googleAnalyticsEvent("booking_process_payment_success", { provider: "gyg" });
      setShowInfoModal(true);
      setShowCheckout(false);
    }
  }, [successfullBooking]);

  useEffect(() => {
    if (
      gygTourData?.availability.available_dates &&
      gygTourData.availability.available_dates.length > 0
    ) {
      const availableDates = gygTourData.availability.available_dates.map(
        item => item.date
      );
      const startMoment = moment(startDate);
      let selectedDate = availableDates[0];

      if (startMoment.isValid()) {
        for (let i = 0; i < availableDates.length; i++) {
          const currentMoment = moment(availableDates[i]);
          if (currentMoment.isSameOrAfter(startMoment)) {
            selectedDate = availableDates[i];
            break;
          }
        }
      }

      setAvailableDate(selectedDate);
    }
  }, [gygTourData?.availability.available_dates, startDate]);

  useEffect(() => {
    if (gygTourData && gygTourData.availability.categories.length > 0) {
      const newPersonsCategories = gygTourData.availability.categories.map(
        x => ({ ...x, count: 0 })
      );

      const adultsCount = adults ? Number(adults) : 0;
      const childrenCount = children ? Number(children) : 0;

      // ADULT
      const newAdultsCategories = newPersonsCategories.find(
        x => x.ticket_category === "adult"
      );
      if (newAdultsCategories) {
        newAdultsCategories.count = adultsCount;
      }

      // CHILD
      const newChildrenCategories = newPersonsCategories.find(
        x => x.ticket_category === "child"
      );
      if (newChildrenCategories) {
        newChildrenCategories.count = childrenCount;
      }

      // GROUP
      const travelerCategory = newPersonsCategories.find(
        x => x.ticket_category === "group"
      );
      if (travelerCategory) {
        travelerCategory.count = adultsCount + childrenCount;
      }

      setPersonsCategories(newPersonsCategories);
    }
  }, [adults, children, gygTourData]);

  // Set first option as default selected when price breakdown is loaded
  useEffect(() => {
    if (
      gygTourPriceBreakdown &&
      gygTourPriceBreakdown.tour_options.length > 0
    ) {
      const firstOption = gygTourPriceBreakdown.tour_options.find(t =>
        t.time_slots.some(
          slot =>
            slot.price_breakdown?.price_summary?.retail_price !== undefined &&
            slot.price_breakdown?.price_summary?.currency !== undefined
        )
      );
      if (firstOption && selectedTourOptionId === null) {
        setSelectedTourOptionId(firstOption.option_id);
      }
    }
  }, [gygTourPriceBreakdown, selectedTourOptionId]);

  const guideType = gygTourData?.tour.guide_type?.replaceAll("_", " ");

  const splitInfo = (info: string | undefined) => {
    if (info) {
      const splittedInfo = info.split(/\r\n|\n|\r/);
      return splittedInfo;
    }
    return [];
  };

  const availableDatesInRange = useMemo(() => {
    if (!gygTourData?.availability.available_dates || !startDate || !endDate) {
      return [];
    }

    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    return gygTourData?.availability.available_dates
      .filter(dateObj => {
        const date = moment(dateObj.date);
        return (
          date.isSameOrAfter(startMoment) && date.isSameOrBefore(endMoment)
        );
      })
      .map(dateObj => dateObj.date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gygTourData?.availability.available_dates, startDate, endDate]);

  const openGallery = (index: number) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  const nextGalleryImage = useCallback(() => {
    setGalleryIndex(
      prev => (prev + 1) % (gygTourData?.tour.pictures.length ?? 1)
    );
  }, [gygTourData?.tour.pictures.length]);

  const prevGalleryImage = useCallback(() => {
    setGalleryIndex(
      prev =>
        (prev - 1 + (gygTourData?.tour.pictures.length ?? 1)) %
        (gygTourData?.tour.pictures.length ?? 1)
    );
  }, [gygTourData?.tour.pictures.length]);

  const forms = (viatorTourDataParam: Providers.Gyg.TourData) => {
    if (successfullBooking) {
      googleAnalyticsDimension(
        "bookedProductUrl",
        viatorTourDataParam?.tour.url
      );
      return null;
    } else {
      return (
        <>
          <GygTourInfoForm
            availableDates={availableDatesInRange}
            availableDate={availableDate}
            setAvailableDate={setAvailableDate}
            formPersonsCategories={viatorTourDataParam?.availability.categories}
            personsCategories={personsCategories}
            setPersonsCategories={setPersonsCategories}
            onScrollToAvailability={() => {
              setTimeout(() => {
                availabilityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 0);
            }}
            checkAvailabilityClick={(
              tourOptionsRequest: Providers.Gyg.TourPriceBreakdownRequest
            ) => {
              setTimeout(() => {
                availabilityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 0);
              if (gygTourData) {
                const totalParticipants = Object.values(
                  tourOptionsRequest.data.participants
                ).reduce((acc, val) => acc + val, 0);
                if (
                  totalParticipants <=
                  gygTourData.availability.participants_range.max
                ) {
                  fetchGygTourPriceBreakdown(
                    gygTourData.tour.tour_id,
                    tourOptionsRequest
                  );
                }
                fetchGygTourOptions(
                  gygTourData.tour.tour_id,
                  availableDate || ""
                );
              }
            }}
            t={t}
          />
        </>
      );
    }
  };

  if (!isOpen) return null;

  if (gygLoaders.tourDataLoader) {
    return (
      <div className="fixed inset-0 bg-gray-50 z-[1100] overflow-y-auto">
        <div className="flex items-center justify-center min-h-[60vh]">
          <PreLoading />
        </div>
      </div>
    );
  }

  if (!gygLoaders.tourDataLoader && !gygTourData) {
    return (
      <>
        <div className="mt-12 mx-auto p-4 text-center">
          {t(
            "trips.myTrips.localExperiences.tourDetails.experience.doesNotSupport"
          )}{" "}
          "GetYourGuide"{" "}
          {t("trips.myTrips.localExperiences.tourDetails.experience.tours")}.
        </div>
      </>
    );
  }

  if (!gygTourData) return null;

  const tourImg = gygTourData.tour.pictures.map(picture => picture.url);
  const formattedImg = helper.getYourGuideImageArrayFormat(tourImg, "146");

  return (
    <>
      {showCheckout ? (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-70 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center relative">
            <div className="bg-white w-full min-h-screen">
              <GygTourCheckout
                clicked={data =>
                  confirmPayment(data, () => {
                    googleAnalyticsEvent("booking_process_payment_fail", { provider: "gyg" });
                    setPaymentFailed(true);
                    setShowCheckout(false);
                  })
                }
                user={user}
                goBack={() => setShowCheckout(false)}
                tourName={gygTourData?.tour.title || ""}
                tourImage={gygTourData?.tour.pictures[0]?.url || ""}
                travelDate={availableDate || ""}
                travelHour={selectedDateTime}
                travelPrice={(() => {
                  const selectedOption =
                    gygTourPriceBreakdown?.tour_options.find(
                      t => t.option_id === selectedTourOptionId
                    );
                  if (selectedOption && selectedOption.time_slots.length > 0) {
                    const firstTimeSlot = selectedOption.time_slots[0];
                    return (
                      firstTimeSlot.price_breakdown?.price_summary
                        ?.retail_price || 0
                    );
                  }
                  return 0;
                })()}
                travelerCount={personsCategories.reduce(
                  (total, category) => total + category.count,
                  0
                )}
                t={t}
                onPaymentError={() => {
                  googleAnalyticsEvent("booking_process_payment_fail", { provider: "gyg" });
                  setPaymentFailed(true);
                  setShowCheckout(false);
                }}
                paymentLoader={gygLoaders.paymentLoader}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          {isGalleryOpen && gygTourData && (
            <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
              <button
                onClick={closeGallery}
                className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
              >
                <SvgIcons.X width="32" height="32" />
              </button>
              <ImageGallery
                images={formattedImg}
                isOpen={isGalleryOpen}
                currentIndex={galleryIndex}
                onClose={closeGallery}
                onChangeIndex={setGalleryIndex}
                onNext={nextGalleryImage}
                onPrev={prevGalleryImage}
              />
            </div>
          )}
          <div className="fixed inset-0 bg-gray-50 z-[1100] overflow-y-auto">
            {/* Sticky header */}
            <div className="sticky top-[60px] md:top-[90px] z-10 bg-gray-50 border-b border-gray-100">
              <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-4">
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50 shadow-sm transition-colors flex-shrink-0"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M19 12H5M12 5l-7 7 7 7"/>
                  </svg>
                </button>
                <span className="text-sm font-medium text-gray-700 truncate">
                  {gygTourData.tour.title}
                </span>
              </div>
            </div>

            {gygLoaders.tourInfoLoader ? (
              <div className="flex items-center justify-center min-h-[60vh]">
                <PreLoading />
              </div>
            ) : gygTourData.tour === undefined ? (
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center p-8">
                  <p className="text-gray-500 mb-4">Your platform does not support "GetYourGuide" tours.</p>
                  <Button color="primary" text="Close" onClick={onClose} />
                </div>
              </div>
            ) : (
              <div className="max-w-6xl mx-auto px-6 pt-10 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] mt-[60px] gap-10 items-start">

                  {/* Left column */}
                  <div className="space-y-8 min-w-0" ref={leftRef}>

                    {/* Image grid - Airbnb style */}
                    <div className="relative rounded-3xl overflow-hidden bg-gray-200" style={{ height: "420px" }}>
                      {formattedImg.length >= 3 ? (
                        <div className="grid grid-cols-2 gap-1 h-full">
                          <div
                            className="relative cursor-pointer overflow-hidden"
                            onClick={() => openGallery(0)}
                          >
                            <img
                              src={formattedImg[0]}
                              alt={gygTourData.tour.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="grid grid-rows-2 gap-1 h-full">
                            <div
                              className="relative cursor-pointer overflow-hidden"
                              onClick={() => openGallery(1)}
                            >
                              <img
                                src={formattedImg[1]}
                                alt=""
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                            <div
                              className="relative cursor-pointer overflow-hidden"
                              onClick={() => openGallery(2)}
                            >
                              <img
                                src={formattedImg[2]}
                                alt=""
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                              />
                              {formattedImg.length > 3 && (
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                  <span className="text-white text-sm font-semibold">
                                    +{formattedImg.length - 3} photos
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="w-full h-full cursor-pointer"
                          onClick={() => openGallery(0)}
                        >
                          <img
                            src={formattedImg[0]}
                            alt={gygTourData.tour.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <button
                        onClick={() => openGallery(0)}
                        className="absolute bottom-4 right-4 bg-white text-gray-800 text-xs font-semibold px-4 py-2 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center gap-1.5"
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
                        </svg>
                        Show all {formattedImg.length} photos
                      </button>
                    </div>

                    {/* Title + Rating */}
                    <div className="space-y-3">
                      <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                        {gygTourData.tour.title}
                      </h1>
                      {gygTourData.tour.number_of_ratings > 0 && (
                        <div className="flex items-center gap-2">
                          <RatingStars
                            rating={(gygTourData.tour.overall_rating * 20).toString()}
                          />
                          {gygTourData.tour.overall_rating && (
                            <span className="font-semibold text-gray-900 text-sm">
                              {gygTourData.tour.overall_rating.toFixed(1)}
                            </span>
                          )}
                          <span className="text-gray-400">·</span>
                          <span className="text-sm text-gray-600 underline cursor-pointer">
                            {gygTourData.tour.number_of_ratings}{" "}
                            {t("trips.myTrips.localExperiences.tourDetails.reviews")}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Quick info chips */}
                    <div className="flex flex-wrap gap-2">
                      {gygTourData.tour.durations.length > 0 && (
                        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                          <span className="flex-shrink-0 text-gray-400 flex items-center">
                            <SvgIcons.Clock2 width="16" height="16" />
                          </span>
                          <span>
                            {gygTourData.tour.durations.map(d => `${d.duration} ${d.unit}`).join(", ")}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                        <span className="flex-shrink-0 text-gray-400 flex items-center">
                          <SvgIcons.Car />
                        </span>
                        <span>{gygTourData.tour.has_pick_up ? "Pickup included" : "No pickup"}</span>
                      </div>
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                        <span className="flex-shrink-0 text-gray-400 flex items-center">
                          <SvgIcons.MobileTicket />
                        </span>
                        <span>{gygTourData.tour.mobile_voucher ? "Mobile Voucher" : "No mobile voucher"}</span>
                      </div>
                      {guideType !== undefined && (
                        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                          <span className="flex-shrink-0 text-gray-400 flex items-center">
                            <SvgIcons.Avatar size="1rem" />
                          </span>
                          <span>{guideType?.charAt(0).toUpperCase() + guideType!.slice(1)}</span>
                        </div>
                      )}
                    </div>

                    <hr className="border-gray-200" />

                    {/* Mobile booking form */}
                    <div className="lg:hidden">
                      {forms(gygTourData)}
                    </div>

                    {/* Availability */}
                    <div ref={availabilityRef} className="scroll-mt-40">
                      {gygLoaders.tourDetailsLoader ? (
                        <div className="flex items-center justify-center py-12">
                          <PreLoading />
                        </div>
                      ) : (
                        <div>
                          {gygTourData?.availability.available_dates &&
                          gygTourData?.availability.available_dates.length > 0 ? (
                            <>
                              {gygTourPriceBreakdown &&
                                gygTourPriceBreakdown.tour_options.length > 0 && (
                                  <div className="w-full">
                                    {gygTourPriceBreakdown.tour_options.filter(
                                      t =>
                                        t.time_slots.some(
                                          slot =>
                                            slot.price_breakdown?.price_summary?.retail_price !== undefined &&
                                            slot.price_breakdown?.price_summary?.currency !== undefined
                                        )
                                    ).length > 0 ? (
                                      gygTourPriceBreakdown.tour_options
                                        .filter(t =>
                                          t.time_slots.some(
                                            slot =>
                                              slot.price_breakdown?.price_summary?.retail_price !== undefined &&
                                              slot.price_breakdown?.price_summary?.currency !== undefined
                                          )
                                        )
                                        .map(tourDataOption => (
                                          <div
                                            className="bg-white rounded-xl py-4 w-full mb-4"
                                            key={tourDataOption.option_id}
                                          >
                                            <GygTourOption
                                              tourPriceBreakdownOption={tourDataOption}
                                              tourOption={gygTourOptions?.find(
                                                t => t.option_id === tourDataOption.option_id
                                              )}
                                              title={gygTourData?.tour.title}
                                              startTime={availableDate}
                                              bookingRequestCallback={async (
                                                bookingRequest: Providers.Gyg.TourBookingRequest
                                              ) => {
                                                try {
                                                  const booking = await gygTourBooking(bookingRequest);
                                                  if (booking === true) {
                                                    setShowCheckout(true);
                                                  }
                                                } catch (error) {
                                                  console.error("Booking error:", error);
                                                }
                                              }}
                                              personsCategories={personsCategories}
                                              participantsRange={gygTourData?.availability.participants_range}
                                              t={t}
                                              isSelected={selectedTourOptionId === tourDataOption.option_id}
                                              setSelectedOption={setSelectedTourOptionId}
                                              selectedDateTime={selectedDateTime}
                                              setSelectedDateTime={setSelectedDateTime}
                                              loading={gygLoaders.bookingLoader}
                                            />
                                          </div>
                                        ))
                                    ) : (
                                      <p className="text-center mb-8 text-red-500">
                                        {t("trips.myTrips.localExperiences.tourDetails.optionNotFound")}.
                                      </p>
                                    )}
                                  </div>
                                )}
                            </>
                          ) : null}
                        </div>
                      )}
                    </div>

                    <hr className="border-gray-200" />

                    {/* About */}
                    <div>
                      <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">
                        {t("trips.myTrips.localExperiences.tourDetails.aboutThisActivity.title")}
                      </h2>
                      <p className="text-gray-600 leading-relaxed text-[15px]">
                        {gygTourData.tour.description}
                      </p>
                    </div>

                    <hr className="border-gray-200" />

                    {/* Mobile: What's included */}
                    <div className="lg:hidden space-y-6">
                      {(splitInfo(gygTourData.tour.inclusions).length > 0 ||
                        splitInfo(gygTourData.tour.exclusions).length > 0) && (
                        <div>
                          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">
                            {t("trips.myTrips.localExperiences.tourDetails.experience.includes")}
                          </h2>
                          <div className="space-y-2">
                            {splitInfo(gygTourData.tour.inclusions).map((inclusion, index) => (
                              <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                                <SvgIcons.Check2 fill="#16a34a" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                                <span>{inclusion}</span>
                              </div>
                            ))}
                            {splitInfo(gygTourData.tour.exclusions).map((exclusion, index) => (
                              <div key={index} className="flex items-start gap-2 text-sm text-gray-500">
                                <SvgIcons.X fill="#9ca3af" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                                <span>{exclusion}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>

                  {/* Right column */}
                  <div className="hidden lg:flex flex-col gap-5 self-start">

                    {/* Booking widget - sticky */}
                    <div className="sticky top-[150px] bg-white rounded-3xl shadow-xl border border-gray-100" ref={formRef}>
                      {forms(gygTourData)}
                    </div>

                    {/* What's included */}
                    {(splitInfo(gygTourData.tour.inclusions).length > 0 ||
                      splitInfo(gygTourData.tour.exclusions).length > 0) && (
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                        <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                          {t("trips.myTrips.localExperiences.tourDetails.experience.includes")}
                        </h3>
                        <div className="space-y-2">
                          {splitInfo(gygTourData.tour.inclusions).map((inclusion, index) => (
                            <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                              <SvgIcons.Check2 fill="#16a34a" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                              <span>{inclusion}</span>
                            </div>
                          ))}
                          {splitInfo(gygTourData.tour.exclusions).map((exclusion, index) => (
                            <div key={index} className="flex items-start gap-2 text-sm text-gray-500">
                              <SvgIcons.X fill="#9ca3af" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                              <span>{exclusion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Highlights */}
                    {gygTourData.tour.highlights && gygTourData.tour.highlights.length > 0 && (
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                        <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                          {t("trips.myTrips.localExperiences.tourDetails.experience.highlights")}
                        </h3>
                        <ul className="space-y-1.5">
                          {gygTourData.tour.highlights.map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="flex-shrink-0 text-gray-400 mt-0.5">›</span>
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Cancellation */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                      <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                        {t("trips.myTrips.localExperiences.tourDetails.aboutThisActivity.cancelation")}
                      </h3>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        {gygTourData.tour.cancellation_policy_text}
                      </div>
                    </div>

                    {/* Additional info */}
                    {gygTourData.tour.additional_information && (
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                        <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                          {t("trips.myTrips.localExperiences.tourDetails.experience.additionalInformation" as any)}
                        </h3>
                        <ul className="space-y-1.5">
                          {splitInfo(gygTourData.tour.additional_information).map((additional_info, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="flex-shrink-0 text-gray-400 mt-0.5">›</span>
                              <span>{additional_info}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  </div>

                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showInfoModal && (
        <Modal
          show={showInfoModal}
          className="!min-w-[90%] !w-[90%] bg-background-color md:!min-w-[50%] md:!w-32 shadow-2xl"
        >
          <div className="row m0 p-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-green-600 mb-4">
                Booking Successful!
              </h2>
              <p className="text-gray-600 mb-4">
                Your booking has been confirmed successfully.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">
                  {gygTourData?.tour.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {availableDate} at {selectedDateTime}
                </p>
                <p className="text-sm text-gray-600">
                  {personsCategories.reduce(
                    (total, category) => total + category.count,
                    0
                  )}{" "}
                  travelers
                </p>
              </div>
            </div>
            <div className="w-full flex justify-center">
              <Button
                color="primary"
                text={t("trips.myTrips.localExperiences.tourDetails.close")}
                onClick={() => {
                  setShowInfoModal(false);
                  onClose();
                }}
              />
            </div>
          </div>
        </Modal>
      )}

      <Modal
        show={paymentFailed}
        className="booking-modal p5 min-w-[90%] md:min-w-0 rounded-3xl"
      >
        <div className="row m0">
          <div className="flex w-full text-center flex-col mb-8 font-bold text-xl mr-1">
            {t("trips.myTrips.localExperiences.tourDetails.paymentFailed")}
          </div>
          <div className="w-full flex justify-evenly gap-4">
            <Button
              className="w-full"
              color="primary"
              text={t("trips.myTrips.itinerary.offers.payment.goBack")}
              onClick={() => {
                setPaymentFailed(false);
                onClose();
              }}
            />
            <Button
              className="w-full !py-[0.8125rem] !px-[1.5rem]"
              type={BUTTON_TYPES.OUTLINED}
              color="default"
              text={t("trips.myTrips.localExperiences.tourDetails.tryAgain")}
              onClick={() => {
                setPaymentFailed(false);
                setShowCheckout(true);
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default GygTourInfo;
